// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { useLocalMessage } from '@Helpers/';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import useValidateForm from '@Hooks/useValidateForm';
import { THForm } from '@Molecules/THForm/THForm';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { Space, Upload } from 'antd';
import { UploadFile } from 'antd/lib/upload/interface';
import { ICONS } from '@Constants/iconsApp';
import { AlertImport } from '@Molecules/AlertImport/AlertImport';

import './AttachmentsCreate.scss';

interface AttachmentCreateFormValues {
    attachments: UploadFile<any>[];
}

interface AttachmentsCreateProps {
    onAddAttachment: (attachment: any) => void;
    triggerElem?: ReactElement;
    defaultVisible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
    visible?: boolean;
    attachmentTab?: 'cases' | 'reports';
}

export const AttachmentsCreate = ({
    onAddAttachment,
    triggerElem,
    defaultVisible = false,
    onVisibleChange,
    visible,
    attachmentTab = 'cases',
}: AttachmentsCreateProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange: onChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const tradKey = attachmentTab === 'cases' ? '' : '.report';

    const onFinish = ({ attachments }: AttachmentCreateFormValues) => {
        const formData = new FormData();

        attachmentTab === 'reports'
            ? attachments.forEach((file) => formData.append('file', file.originFileObj as any))
            : attachments.forEach((file) => formData.append('attachments', file.originFileObj as any));

        onAddAttachment(formData);
        setFieldsTouched(false);
    };

    return (
        <THDrawer
            title={i(`attachment.create${tradKey}.title`)}
            triggerElem={triggerElem}
            defaultVisible={defaultVisible}
            afterVisibleChange={(visible) => {
                form.resetFields();
                onVisibleChange?.(visible);
            }}
            className="drawer-form"
            disableClose={fieldsTouched}
            visible={visible}
        >
            {(toggleDrawer) => (
                <THForm<AttachmentCreateFormValues>
                    name="attachment-form"
                    form={form}
                    onFinish={(values) => {
                        onFinish(values);
                        toggleDrawer();
                    }}
                    onValuesChange={() => {
                        onChange();
                        setFieldsTouched(true);
                    }}
                    className="attachments-create"
                >
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                onCancel={() => {
                                    setFieldsTouched(false);
                                    form.resetFields();
                                    toggleDrawer();
                                }}
                                disabled={!isValid || !fieldsTouched}
                                footerText={i('general.confirm')}
                            />
                        }
                    >
                        <Space direction="vertical" className="file-import__container" size="large">
                            <AlertImport title={i(`attachment.create${tradKey}.title`)}>
                                <>{i(`attachment.import${tradKey}.content`)}</>
                            </AlertImport>
                            <div className="file-import__upload">
                                <span className="file-import__upload-title">
                                    {i(tradKey ? `attachment.import${tradKey}.label` : 'general.import.upload.label')}
                                </span>
                                <THForm.Item
                                    name="attachments"
                                    rules={[
                                        {
                                            required: true,
                                            message: i('attachment.create.required'),
                                        },
                                    ]}
                                    valuePropName="fileList"
                                    getValueFromEvent={(e: any) => (Array.isArray(e) ? e : e && e.fileList)}
                                >
                                    <Upload.Dragger
                                        beforeUpload={() => false}
                                        multiple
                                        className="attachments-create__upload-list"
                                        showUploadList={{
                                            showDownloadIcon: false,
                                            showRemoveIcon: true,
                                            removeIcon: <ICONS.DeleteIcon />,
                                        }}
                                    >
                                        {i('general.import.upload.content')}
                                    </Upload.Dragger>
                                </THForm.Item>
                            </div>
                        </Space>
                    </THForm.Layout>
                </THForm>
            )}
        </THDrawer>
    );
};
